import type { getHomepageContentResponse } from '#/api/generated/content/content';
import { useGetHomepageContent } from '#/api/generated/content/content';
import type { ContentPage } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type { ContentArticleCard, StaticFallback } from '#/content/lib';
import {
  contentArticleCard,
  contentSection,
  contentTextPlain,
  drupalLinkHref,
  isContentPage,
} from '#/content/lib';
import { CONTENT_CACHE_QUERY_POLICY } from '#/content/queries';
import { shouldUseFallbackContent } from '#/lib/api-client';
import { APP_NAME } from '#/utils/constants';

/**
 * The homepage content document: view model, fallback copy, merge, and hook.
 */
export interface HomepageContentView {
  heroTitle: string;
  heroDescription: string;
  trustSignals: string[];
  exploreConcepts: ExploreConceptsView | null;
  news: HomepageNewsView | null;
  aboutFinra: AboutFinraView | null;
}

export interface AboutFinraView {
  /** Editorial headline, e.g. "Responsibility Is Our Job". */
  subtitle: string;
  description: string;
  /**
   * CTA to the host site. Falls back to the FINRA about page (with the
   * authored label, or an empty label) when the authored link can't render as
   * an href.
   */
  link: { label: string; href: string } | null;
}

export interface ExploreConceptsView {
  title: string;
  description: string;
  articles: ContentArticleCard[];
}

export interface NewsItemLink {
  label: string;
  href: string;
}

export interface NewsItemView {
  /** Drupal paragraph id — stable render key. */
  id: string;
  title: string;
  content: string;
  /** Security ticker for a live price summary, when the item references one. */
  ticker: string | null;
  links: NewsItemLink[];
}

export interface HomepageNewsView {
  /** Editorial section heading, e.g. "In the News". */
  title: string | null;
  items: NewsItemView[];
}

/**
 * Tier 1 Fallback homepage copy, never null
 */
export const HOMEPAGE_FALLBACK: StaticFallback<HomepageContentView> = {
  heroTitle: 'Research Stocks, Mutual Funds, and ETFs',
  heroDescription: `FINRA's ${APP_NAME} gives investors free access to unbiased market data and investment research to make informed decisions.`,
  trustSignals: ['Free for everyone', 'Beginner-friendly', 'Trustworthy'],
};

/**
 * @TODO(nlong): Add icon selection once mismatch addressed
 * Normalize the `value_statements` section into plain trust-signal labels.
 */
function normalizeTrustSignals(
  page: ContentPage | null | undefined,
): string[] | null {
  const items = contentSection(page, 'value_statements')?.items ?? [];

  const labels = items
    .map((item) => item.short_text?.trim() || contentTextPlain(item.text))
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels : null;
}

/**
 * Normalize the explore-concepts section into renderable article cards.
 */
function normalizeExploreConcepts(
  page: ContentPage | null | undefined,
): ExploreConceptsView | null {
  const title = page?.section_title?.trim();
  const description = contentTextPlain(page?.section_description);
  if (!title || !description) {
    return null;
  }

  const items = contentSection(page, 'explore_concepts')?.items ?? [];
  const articles = items
    .flatMap((item) => item.sections ?? [])
    .flatMap((section) => section.items ?? [])
    .flatMap((article) => {
      const card = contentArticleCard(article);
      return card ? [card] : [];
    });

  return articles.length > 0 ? { title, description, articles } : null;
}

/**
 * Normalize the in-the-news section into renderable news cards.
 */
function normalizeNews(
  page: ContentPage | null | undefined,
): HomepageNewsView | null {
  const section = contentSection(page, 'news');

  const items = (section?.items ?? []).flatMap((item) => {
    const title = item.title?.trim();
    const content = contentTextPlain(item.text);
    if (!title || !content) {
      return [];
    }

    const links = (item.links ?? []).flatMap((link) => {
      const label = link.title?.trim();
      const href = drupalLinkHref(link.uri);

      return label && href ? [{ label, href }] : [];
    });

    return [
      {
        id: item.id,
        title,
        content,
        ticker: item.ticker?.trim() || null,
        links,
      },
    ];
  });

  return items.length > 0
    ? { title: section?.title?.trim() || null, items }
    : null;
}

/**
 * Normalize the top-level mission fields (`subtitle`, `description`, `link`)
 * into the More About FINRA section.
 */
function normalizeAboutFinra(
  page: ContentPage | null | undefined,
): AboutFinraView | null {
  const subtitle = page?.subtitle?.trim();
  const description = contentTextPlain(page?.description);
  if (!subtitle || !description) {
    return null;
  }

  const label = page?.link?.title?.trim();
  const href = drupalLinkHref(page?.link?.uri);

  return {
    subtitle,
    description,
    link:
      label && href
        ? { label, href }
        : { label: label ?? '', href: 'https://www.finra.org/about' },
  };
}

/**
 * Merge fields and fallbacks, null is the total-failure state
 */
export function mergeHomepage(
  page: ContentPage | null | undefined,
): HomepageContentView {
  return {
    heroTitle:
      page?.home_page_title ?? page?.title ?? HOMEPAGE_FALLBACK.heroTitle,
    heroDescription:
      contentTextPlain(page?.body) ?? HOMEPAGE_FALLBACK.heroDescription,
    trustSignals: normalizeTrustSignals(page) ?? HOMEPAGE_FALLBACK.trustSignals,
    exploreConcepts: normalizeExploreConcepts(page),
    news: normalizeNews(page),
    aboutFinra: normalizeAboutFinra(page),
  };
}

interface UseHomepageContentResult {
  content: HomepageContentView;
  /** True when the content API failed and fallback copy is being shown. */
  isDegraded: boolean;
}

function selectHomepageContent(
  response: getHomepageContentResponse,
): HomepageContentView {
  return mergeHomepage(isContentPage(response.data) ? response.data : null);
}

/**
 * Homepage content, merged against fallback copy.
 *
 * Warmed via `homepageContentOptions()` on `/` with `staleTime: Infinity` for
 * no re-renders for the life of the session.
 */
export function useHomepageContent(): UseHomepageContentResult {
  const query = useGetHomepageContent({
    query: {
      ...CONTENT_CACHE_QUERY_POLICY,
      select: selectHomepageContent,
    },
  });

  const content = query.isSuccess ? query.data : mergeHomepage(null);

  return {
    content,
    isDegraded: shouldUseFallbackContent(query.error),
  };
}
